import { IdentityAvatar } from '@/heroui/IdentityAvatar';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { shortTimeLabel } from '@/lib/messagePreview';
import { QUICK_REACTIONS } from '@/lib/permissions';
import { ReplyIcon, SmileIcon, TrashIcon } from '@/components/icons';
import { AttachmentList, parseAttachments } from '@/components/AttachmentList';
import type { Message } from '@/types/api';

/** One comment in the thread/discussion panel: avatar + author + time, an
 *  optional reply quote, body, attachments, reaction chips, and hover actions
 *  (react / reply / delete). The root message is rendered with no actions. */
export function CommentRow({
  message,
  myId,
  onReply,
  onReact,
  onDelete,
}: {
  message: Message;
  myId: string | null;
  onReply?: () => void;
  onReact?: (emoji: string) => void;
  onDelete?: () => void;
}) {
  const { t } = useTranslation();
  const author = message.author;
  const name = author.displayName || author.username;
  const mine = author.id === myId;
  const [pickerOpen, setPickerOpen] = useState(false);
  const reply = message.replyTo;
  return (
    <div className="group/cm flex items-start gap-2.5">
      <IdentityAvatar id={author.id} name={name} src={author.avatarUrl} size="sm" />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className={'text-sm font-medium ' + (mine ? 'text-accent' : 'text-foreground')}>
            {name}
          </span>
          <span className="text-[10px] tabular-nums text-muted">
            {shortTimeLabel(message.createdAt)}
          </span>
        </div>
        {reply && (
          <div className="mt-0.5 flex items-center gap-1 truncate border-l-2 border-separator pl-2 text-xs text-muted">
            <span className="font-medium text-foreground/70">
              {reply.author.displayName || reply.author.username}
            </span>
            <span className="truncate">{reply.deletedAt ? t('prof.thread.messageDeleted') : reply.content}</span>
          </div>
        )}
        {message.content.trim() && (
          <div className="whitespace-pre-wrap break-words text-sm text-foreground/90">
            {message.content}
          </div>
        )}
        <AttachmentList attachments={parseAttachments(message.attachments)} />
        {message.reactions.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1">
            {message.reactions.map((r) => {
              const reactedByMe = !!myId && r.users.includes(myId);
              return (
                <button
                  key={r.emoji}
                  type="button"
                  disabled={!onReact}
                  onClick={() => onReact?.(r.emoji)}
                  className={
                    'flex h-6 items-center gap-1 rounded-full border px-2 text-xs transition-colors ' +
                    (reactedByMe
                      ? 'border-accent/40 bg-accent/15 text-accent'
                      : 'border-separator bg-default/40 text-muted hover:bg-default/60')
                  }
                >
                  <span className="leading-none">{r.emoji}</span>
                  <span className="tabular-nums leading-none">{r.count}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {(onReply || onReact || onDelete) && (
        <div className="relative mt-0.5 flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover/cm:opacity-100 group-focus-within/cm:opacity-100 [@media(pointer:coarse)]:opacity-100">
          {onReact && (
            <>
              <button
                type="button"
                aria-label={t('prof.thread.reaction')}
                onClick={() => setPickerOpen((v) => !v)}
                className="rounded-md p-1 text-muted hover:bg-default/60 hover:text-foreground"
              >
                <SmileIcon className="h-3.5 w-3.5" />
              </button>
              {pickerOpen && (
                <div className="bg-surface absolute right-0 top-full z-10 mt-1 flex items-center gap-0.5 rounded-full border border-separator p-0.5 shadow-card">
                  {QUICK_REACTIONS.map((emoji) => (
                    <button
                      key={emoji}
                      type="button"
                      onClick={() => {
                        setPickerOpen(false);
                        onReact(emoji);
                      }}
                      className="grid h-7 w-7 place-items-center rounded-full text-base transition-transform hover:scale-110 hover:bg-white/10"
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
          {onReply && (
            <button
              type="button"
              aria-label={t('prof.thread.reply')}
              onClick={onReply}
              className="rounded-md p-1 text-muted hover:bg-default/60 hover:text-foreground"
            >
              <ReplyIcon className="h-3.5 w-3.5" />
            </button>
          )}
          {onDelete && (
            <button
              type="button"
              aria-label={t('prof.thread.deleteComment')}
              onClick={onDelete}
              className="rounded-md p-1 text-muted hover:bg-danger/10 hover:text-danger"
            >
              <TrashIcon className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
